/**
 * A help article's media (CLA-1684): a how-to video with captions, and the
 * step screenshots it was rendered with. Both are produced by script
 * (`web/guides/`), never recorded by hand, and arrive in the article's
 * `media` field from GET /help/articles/:slug.
 *
 * Hosting note: captions on another origin need `crossorigin`, so the media
 * host must send CORS headers or the browser drops the caption track.
 */

/** Shape of claro-elixir's `HelpMedia` schema (priv/openapi/v1.json). */
export interface HelpMediaData {
  video: { url: string; poster: string | null; captions: string | null } | null
  steps: { image: string; caption: string }[]
}

export function HelpMedia({ media, title }: { media: HelpMediaData; title: string }) {
  const { video, steps } = media
  if (!video && steps.length === 0) return null

  return (
    <div className="space-y-5">
      {video && (
        <video
          controls
          playsInline
          preload="metadata"
          poster={video.poster ?? undefined}
          crossOrigin={video.captions ? 'anonymous' : undefined}
          aria-label={`Video: ${title}`}
          className="w-full aspect-[16/10] rounded-lg border border-border-subtle bg-surface-secondary"
        >
          <source src={video.url} type="video/mp4" />
          {video.captions && <track kind="captions" src={video.captions} srcLang="en" label="English" default />}
          {/* Shown only by browsers that can't play the video. */}
          <a href={video.url} className="text-sm link">
            Download the video
          </a>
        </video>
      )}

      {steps.length > 0 && (
        <section aria-label="Step by step">
          <h3 className="text-sm font-semibold text-ink mb-3">Step by step</h3>
          <ol className="space-y-4">
            {steps.map((step, i) => (
              <li key={`${i}-${step.image}`}>
                <figure>
                  <img
                    src={step.image}
                    alt={`Step ${i + 1} screenshot`}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-auto rounded-lg border border-border-subtle"
                  />
                  <figcaption className="mt-1.5 text-sm text-ink-secondary leading-relaxed">
                    <span className="font-medium text-ink">{i + 1}.</span> {step.caption}
                  </figcaption>
                </figure>
              </li>
            ))}
          </ol>
        </section>
      )}
    </div>
  )
}
